// ============================================================
// PlatformAuditTemplatesPage.js
// Route /platform/audit-templates — list with filter bar, view
// detail panel, metadata-only Add/Edit, toggle active, plus the
// version lifecycle: per-row Builder (AuditTemplateBuilder, the
// full section/question editor), New version (clone into an
// inactive draft) and Publish (activate a draft; the previously
// active version retires and schedules repoint).
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Eye } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Switch from '../components/Switch';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Field from '../components/Field';
import Badge from '../components/Badge';
import { toast } from '../components/Toaster';
import {
  listPlatformAuditTemplates, getPlatformAuditTemplate,
  createPlatformAuditTemplate, patchPlatformAuditTemplate,
  createPlatformAuditTemplateVersion, publishPlatformAuditTemplate,
} from '../api/adminPlatform';
import { translateError } from '../utils/errorMessages';
import StatusMessage from '../components/StatusMessage';
import AuditTemplateBuilder from './AuditTemplateBuilder';
import './PlatformPages.css';

const SETTING_OPTIONS = [
  { value: '',            label: 'All settings' },
  { value: 'both',        label: 'Both' },
  { value: 'residential', label: 'Residential' },
  { value: 'domiciliary', label: 'Domiciliary' },
];
const VARIANT_OPTIONS = [
  { value: '',      label: 'All variants' },
  { value: 'quick', label: 'Quick' },
  { value: 'full',  label: 'Full' },
];
const BLANK_FORM = {
  code: '', title: '', description: '',
  applicableSetting: 'both', auditCategory: 'care_delivery', templateVariant: 'quick',
  greenThresholdPercent: 80, amberThresholdPercent: 60,
};

export default function PlatformAuditTemplatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const [filterSetting, setFilterSetting] = useState('');
  const [filterVariant, setFilterVariant] = useState('');
  const [search, setSearch] = useState('');

  // Detail panel state.
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Add / Edit modal.
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Structure builder + version lifecycle.
  const [builderId, setBuilderId] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listPlatformAuditTemplates();
      setItems(res.items || []);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterSetting && it.applicableSetting !== filterSetting) return false;
      if (filterVariant && it.templateVariant !== filterVariant) return false;
      if (search && !it.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, filterSetting, filterVariant, search]);

  async function openDetail(row) {
    setDetail({ auditTemplateId: row.auditTemplateId, header: row });
    setDetailLoading(true); setDetailError(null);
    try {
      const full = await getPlatformAuditTemplate(row.auditTemplateId);
      setDetail({ auditTemplateId: row.auditTemplateId, header: row, body: full });
    } catch (err) { setDetailError(translateError(err)); }
    finally { setDetailLoading(false); }
  }

  function openAdd() {
    setEditingId(null); setForm(BLANK_FORM); setFormError(null); setIsFormOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.auditTemplateId);
    setForm({
      code: row.code,
      title: row.title,
      description: row.description || '',
      applicableSetting: row.applicableSetting,
      auditCategory: row.auditCategory || 'care_delivery',
      templateVariant: row.templateVariant,
      greenThresholdPercent: row.greenThresholdPercent,
      amberThresholdPercent: row.amberThresholdPercent,
    });
    setFormError(null);
    setIsFormOpen(true);
  }
  function closeForm() {
    if (submitting) return;
    setIsFormOpen(false); setEditingId(null); setForm(BLANK_FORM);
  }

  async function submitForm(e) {
    e.preventDefault();
    setFormError(null); setSubmitting(true);
    try {
      if (editingId) {
        const { code: _omit, ...patch } = form;
        await patchPlatformAuditTemplate(editingId, patch);
        toast.success('Audit template updated');
      } else {
        // Metadata-only create. Backend requires sections to ship —
        // emit a single section with one placeholder question so the
        // sum-to-100 invariant passes. The operator then replaces the
        // placeholder in the template builder.
        const placeholderBody = {
          ...form,
          sections: [
            {
              code: 'placeholder',
              title: 'Pending content',
              weightingPercent: 100,
              questions: [
                {
                  code: 'placeholder_q',
                  questionText: 'Placeholder question - replace it in the template builder.',
                },
              ],
            },
          ],
        };
        await createPlatformAuditTemplate(placeholderBody);
        toast.success('Audit template created. Open the builder to add its sections and questions.');
      }
      setIsFormOpen(false); setEditingId(null); setForm(BLANK_FORM);
      await fetchData();
    } catch (err) { setFormError(translateError(err)); }
    finally { setSubmitting(false); }
  }

  // Clone a template into a new inactive draft, then open the
  // builder ON THE RETURNED DRAFT id. 409s (not_latest_version /
  // draft_already_exists) surface via translateError's curated
  // entries.
  const handleNewVersion = useCallback(async (templateId) => {
    setPendingId(templateId);
    try {
      const res = await createPlatformAuditTemplateVersion(templateId);
      toast.success(`Draft v${res.version} created. Opening the builder.`);
      setBuilderId(res.auditTemplateId);
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPendingId(null); }
  }, [fetchData]);

  async function confirmPublish() {
    if (!publishTarget) return;
    setPublishing(true);
    try {
      await publishPlatformAuditTemplate(publishTarget.auditTemplateId);
      toast.success('Template version published.');
      setPublishTarget(null);
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPublishing(false); }
  }

  async function toggleActive(row, nextActive) {
    // ACTIVATION routes through the Publish confirm, never a bare PATCH —
    // and the backend now enforces this (409 use_publish): publishing
    // retires the previously-active version of the same code and repoints
    // schedules atomically, which a raw isActive flip would bypass (two
    // live versions of one code in every client's picker).
    if (nextActive) { setPublishTarget(row); return; }
    setPendingId(row.auditTemplateId);
    try {
      await patchPlatformAuditTemplate(row.auditTemplateId, { isActive: false });
      toast.success('Template deactivated');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPendingId(null); }
  }

  // An inactive row is a DRAFT only when it is the newest version of its
  // code; an inactive OLDER version is a retired predecessor and labelling
  // it "draft" would invite editing history.
  const maxVersionByCode = useMemo(() => {
    const m = new Map();
    for (const r of items) {
      if (r.version != null) m.set(r.code, Math.max(m.get(r.code) || 0, r.version));
    }
    return m;
  }, [items]);

  const columns = [
    { key: 'title', label: 'Title', render: (r) => r.title },
    { key: 'version', label: 'Version', width: '100px',
      render: (r) => {
        if (r.version == null) return '';
        if (r.isActive) return `v${r.version}`;
        return `v${r.version}${r.version === maxVersionByCode.get(r.code) ? ' (draft)' : ' (retired)'}`;
      },
    },
    { key: 'variant', label: 'Variant', width: '90px',
      render: (r) => <Badge variant={r.templateVariant === 'full' ? 'info' : 'neutral'}>{r.templateVariant === 'full' ? 'Full' : 'Quick'}</Badge>,
    },
    { key: 'setting', label: 'Setting', width: '110px',
      render: (r) => <Badge variant="neutral">{r.applicableSetting}</Badge>,
    },
    { key: 'thresholds', label: 'Thresholds', width: '140px',
      render: (r) => <span style={{ fontSize: 12 }}>G ≥ {r.greenThresholdPercent}% / A ≥ {r.amberThresholdPercent}%</span>,
    },
    { key: 'sections', label: 'Sections', render: (r) => r.sectionCount, width: '90px', align: 'right' },
    { key: 'questions', label: 'Questions', render: (r) => r.questionCount, width: '100px', align: 'right' },
    { key: 'active', label: 'Status', width: '90px',
      render: (r) => (
        <Switch
          checked={r.isActive}
          onChange={(next) => toggleActive(r, next)}
          disabled={pendingId === r.auditTemplateId}
          ariaLabel={`${r.code} active`}
        />
      ),
    },
    { key: 'actions', label: '', width: '320px',
      render: (r) => (
        <div className="platform-page__row-actions">
          <button type="button" className="platform-page__row-action" onClick={() => openDetail(r)} title="View">
            <Eye size={12} /> View
          </button>
          <button type="button" className="platform-page__row-action" onClick={() => openEdit(r)}>
            Edit
          </button>
          <button type="button" className="platform-page__row-action" onClick={() => setBuilderId(r.auditTemplateId)}>
            Builder
          </button>
          <button
            type="button" className="platform-page__row-action"
            onClick={() => handleNewVersion(r.auditTemplateId)}
            disabled={pendingId === r.auditTemplateId}
          >
            New version
          </button>
          {r.isActive === false && (
            <button type="button" className="platform-page__row-action" onClick={() => setPublishTarget(r)}>
              Publish
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Audit Templates"
        subtitle="CareAudit platform catalogue. Add Template captures metadata only; open Builder on a row to edit its sections and questions, then Publish the draft."
        cta={(
          <button type="button" className="platform-page__cta" onClick={openAdd}>
            <Plus size={16} /> Add Template
          </button>
        )}
      />

      <div className="platform-page__filter-bar">
        <select value={filterSetting} onChange={(e) => setFilterSetting(e.target.value)}>
          {SETTING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filterVariant} onChange={(e) => setFilterVariant(e.target.value)}>
          {VARIANT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          type="search"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        rowKey={(r) => r.auditTemplateId}
        loading={loading}
        error={error}
        onRetry={fetchData}
        totalCount={filtered.length}
        page={1}
        pageSize={filtered.length || 1}
        onPageChange={() => {}}
        emptyMessage={items.length === 0 ? 'No audit templates defined.' : 'No matches for the current filters.'}
      />

      {/* Detail modal — read-only sections/questions tree. */}
      <Modal
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.header.title} — ${detail.header.templateVariant} variant` : ''}
      >
        {detailLoading && <StatusMessage as="p" variant="loading">Loading template detail…</StatusMessage>}
        {detailError && <StatusMessage as="p" variant="error" className="platform-page__form-error">{detailError}</StatusMessage>}
        {detail && detail.body && (
          <div>
            <p style={{ marginTop: 0, color: 'var(--color-text-muted)' }}>
              Code: <code className="platform-page__code">{detail.body.code}</code>{' '}
              · Setting: {detail.body.applicableSetting}{' '}
              · Category: {detail.body.auditCategory}{' '}
              · Thresholds: G ≥ {detail.body.greenThresholdPercent}% / A ≥ {detail.body.amberThresholdPercent}%
            </p>
            {(detail.body.sections || []).map((s) => (
              <div key={s.auditTemplateSectionId} style={{ marginBottom: 16 }}>
                <h4 style={{ marginBottom: 4 }}>{s.title} ({s.weightingPercent}%)</h4>
                {s.description && <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 0 }}>{s.description}</p>}
                <ol style={{ paddingLeft: 20, fontSize: 13 }}>
                  {(s.questions || []).map((q) => (
                    <li key={q.auditTemplateQuestionId} style={{ marginBottom: 8 }}>
                      <div>{q.questionText}</div>
                      {q.guidanceNotes && <div style={{ fontStyle: 'italic', color: 'var(--color-text-muted)' }}>{q.guidanceNotes}</div>}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
            {(!detail.body.sections || detail.body.sections.length === 0) && (
              <p style={{ color: 'var(--color-text-muted)' }}>No sections defined yet.</p>
            )}
          </div>
        )}
      </Modal>

      {/* Add / Edit metadata-only form modal. */}
      <Modal
        isOpen={isFormOpen}
        onClose={closeForm}
        title={editingId ? 'Edit template metadata' : 'Add audit template'}
        closeDisabled={submitting}
      >
        <form onSubmit={submitForm} className="platform-page__form">
          {!editingId && (
            <p className="platform-page__warn" style={{ background: '#FFF8E1', padding: 8, borderRadius: 6 }}>
              A placeholder section will be created so the template saves cleanly.
              Open Builder on the new row afterwards to add the real sections and questions.
            </p>
          )}
          <Field label="Code" required help="Lower-snake-case, 3-100 chars. Immutable after creation.">
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={Boolean(editingId)}
              maxLength={100}
              required
              pattern="^[a-z0-9_]{3,100}$"
            />
          </Field>
          <Field label="Title" required>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={500} required />
          </Field>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              maxLength={4000}
            />
          </Field>
          <Field label="Applicable Setting" required>
            <select value={form.applicableSetting} onChange={(e) => setForm({ ...form, applicableSetting: e.target.value })}>
              <option value="both">Both</option>
              <option value="residential">Residential</option>
              <option value="domiciliary">Domiciliary</option>
            </select>
          </Field>
          <Field label="Audit Category" required>
            <select value={form.auditCategory} onChange={(e) => setForm({ ...form, auditCategory: e.target.value })}>
              <option value="care_delivery">Care delivery</option>
              <option value="provider_governance">Provider governance</option>
              <option value="business_compliance">Business compliance</option>
            </select>
          </Field>
          <Field label="Variant" required>
            <select value={form.templateVariant} onChange={(e) => setForm({ ...form, templateVariant: e.target.value })}>
              <option value="quick">Quick</option>
              <option value="full">Full</option>
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Green threshold %" required>
              <input
                type="number" min={0} max={100}
                value={form.greenThresholdPercent}
                onChange={(e) => setForm({ ...form, greenThresholdPercent: parseInt(e.target.value, 10) || 0 })}
                required
              />
            </Field>
            <Field label="Amber threshold %" required help="Must be below green.">
              <input
                type="number" min={0} max={100}
                value={form.amberThresholdPercent}
                onChange={(e) => setForm({ ...form, amberThresholdPercent: parseInt(e.target.value, 10) || 0 })}
                required
              />
            </Field>
          </div>
          {formError && <p className="platform-page__form-error" role="alert">{formError}</p>}
          <div className="platform-page__form-actions">
            <button type="button" onClick={closeForm} disabled={submitting} className="platform-page__btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="platform-page__btn-primary">
              {submitting ? 'Saving…' : (editingId ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Full structure builder (wide modal, own component). */}
      <AuditTemplateBuilder
        isOpen={builderId != null}
        templateId={builderId}
        onClose={() => setBuilderId(null)}
        onSaved={fetchData}
        onCreateNewVersion={handleNewVersion}
      />

      {/* Publish confirm — activating a draft retires the previously
          active version, so it needs an explicit confirmation. */}
      <ConfirmDialog
        isOpen={Boolean(publishTarget)}
        onClose={() => { if (!publishing) setPublishTarget(null); }}
        onConfirm={confirmPublish}
        title="Publish template version"
        message={publishTarget
          ? `Publish "${publishTarget.title}" v${publishTarget.version}? The previously active version of this template retires, and any audit schedules pointing at it repoint to this version.`
          : ''}
        confirmLabel="Publish version"
        tone="primary"
        isSubmitting={publishing}
      />
    </>
  );
}
